import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, TrendingUp, Users, DollarSign, Clock, CheckCircle2, XCircle, AlertCircle, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateBR } from '../utils/dateFormat';
import { useContext } from 'react';
import { AuthContext } from '../App';

export default function Relatorios() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodo, setPeriodo] = useState('mes');
  const [tipoCliente, setTipoCliente] = useState('todos');
  const [status, setStatus] = useState('todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    // Aplicar filtros da URL se existirem
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setStatus(statusParam);
    }
    carregarRelatorio();
    
    // Listener para atualizar em tempo real quando houver mudanças
    const handleSolicitacaoModificada = () => {
      carregarRelatorio();
    };
    
    window.addEventListener('solicitacaoModificada', handleSolicitacaoModificada);
    
    return () => {
      window.removeEventListener('solicitacaoModificada', handleSolicitacaoModificada);
    };
  }, [periodo, tipoCliente, status, dataInicio, dataFim]);

  const carregarRelatorio = async () => {
    const isFirstLoad = !dados;
    if (isFirstLoad) setLoading(true);
    
    try {
      const params = new URLSearchParams();
      
      // Verificar se há filtro de status vindo da URL
      const statusParam = searchParams.get('status');
      const statusFiltro = statusParam || status;
      
      if (periodo !== 'custom') {
        params.append('periodo', periodo);
      } else if (dataInicio && dataFim) {
        params.append('data_inicio', dataInicio);
        params.append('data_fim', dataFim);
      }
      
      if (tipoCliente && tipoCliente !== 'todos') params.append('tipo_cliente', tipoCliente);
      if (statusFiltro && statusFiltro !== 'todos') params.append('status', statusFiltro);

      const res = await axios.get(`${API}/relatorios/filtrado?${params.toString()}`);
      setDados(res.data);
    } catch (error) {
      // Silenciar erros em atualizações automáticas
      if (isFirstLoad) {
        toast.error('Erro ao carregar relatório');
      }
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  };

  const handleFiltrar = () => {
    carregarRelatorio();
  };

  if (loading && !dados) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="relatorios-page">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-1 lg:mb-2">Relatórios</h1>
        <p className="text-sm sm:text-base text-slate-600">Análise detalhada das reservas e desempenho</p>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label>Período</Label>
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger data-testid="select-periodo">
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">Hoje</SelectItem>
                  <SelectItem value="semana">Esta Semana</SelectItem>
                  <SelectItem value="mes">Este Mês</SelectItem>
                  <SelectItem value="ano">Este Ano</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {periodo === 'custom' && (
              <>
                <div>
                  <Label>Data Início</Label>
                  <Input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    data-testid="input-data-inicio"
                  />
                </div>
                <div>
                  <Label>Data Fim</Label>
                  <Input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    data-testid="input-data-fim"
                  />
                </div>
              </>
            )}

            <div>
              <Label>Tipo de Cliente</Label>
              <Select value={tipoCliente} onValueChange={setTipoCliente}>
                <SelectTrigger data-testid="select-tipo-cliente">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Associado">Associado</SelectItem>
                  <SelectItem value="Voucher">Voucher</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="select-status">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Pendente">Pendente</SelectItem>
                  <SelectItem value="Em Tratamento">Em Tratamento</SelectItem>
                  <SelectItem value="Montada">Montada</SelectItem>
                  <SelectItem value="Enviada">Enviada</SelectItem>
                  <SelectItem value="Concluída">Concluída</SelectItem>
                  <SelectItem value="Cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleFiltrar}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="filtrar-btn"
              >
                {loading ? 'Carregando...' : 'Filtrar'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {dados && (
        <>
          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
            <Card data-testid="card-total">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total de Reservas</CardTitle>
                <FileText className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{dados.total_solicitacoes}</div>
              </CardContent>
            </Card>

            <Card data-testid="card-concluidas">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Concluídas</CardTitle>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{dados.por_status.concluidas}</div>
              </CardContent>
            </Card>

            <Card data-testid="card-faturamento">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Receita (Cliente)</CardTitle>
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-800">R$ {dados.faturamento.total.toFixed(2)}</div>
              </CardContent>
            </Card>

            <Card data-testid="card-custo">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Custo (Fornecedor)</CardTitle>
                <TrendingDown className="h-5 w-5 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-800">R$ {(dados.faturamento.custo_fornecedor || 0).toFixed(2)}</div>
              </CardContent>
            </Card>

            <Card data-testid="card-lucro">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Lucro Líquido</CardTitle>
                <TrendingUp className={`h-5 w-5 ${(dados.faturamento.lucro || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${(dados.faturamento.lucro || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  R$ {(dados.faturamento.lucro || 0).toFixed(2)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status e Tipos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader>
                <CardTitle>Por Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Pendentes</span>
                    <Badge className="bg-yellow-100 text-yellow-800">{dados.por_status.pendentes || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Em Tratamento</span>
                    <Badge className="bg-blue-100 text-blue-800">{dados.por_status.em_tratamento || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Montadas</span>
                    <Badge className="bg-purple-100 text-purple-800">{dados.por_status.montadas || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Enviadas</span>
                    <Badge className="bg-cyan-100 text-cyan-800">{dados.por_status.enviadas || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Concluídas</span>
                    <Badge className="bg-green-100 text-green-800">{dados.por_status.concluidas || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Canceladas</span>
                    <Badge className="bg-red-100 text-red-800">{dados.por_status.canceladas || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por Tipo de Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Associados</span>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-800">{dados.por_tipo_cliente.associados}</div>
                      <div className="text-sm text-slate-500">R$ {dados.faturamento.associados.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="h-px bg-slate-200"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Vouchers</span>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-800">{dados.por_tipo_cliente.vouchers}</div>
                      <div className="text-sm text-slate-500">R$ {dados.faturamento.vouchers.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tipo de Uso */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Por Tipo de Uso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(dados.por_tipo_uso).map(([tipo, qtd]) => (
                  <div key={tipo} className="text-center p-4 bg-slate-50 rounded-lg">
                    <div className="text-2xl font-bold text-slate-800">{qtd}</div>
                    <div className="text-sm text-slate-600">{tipo}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Desempenho por Atendente (apenas gerentes) */}
          {user?.funcao === 'gerente' && dados.por_atendente.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Desempenho por Atendente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs xl:text-sm">Atendente</TableHead>
                        <TableHead className="text-xs xl:text-sm">Total</TableHead>
                        <TableHead className="text-xs xl:text-sm hidden lg:table-cell">Concluídas</TableHead>
                        <TableHead className="text-xs xl:text-sm">Faturamento</TableHead>
                        <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Tempo Médio</TableHead>
                        <TableHead className="text-xs xl:text-sm hidden lg:table-cell">Taxa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dados.por_atendente.map((atendente, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium text-xs xl:text-sm">{atendente.nome}</TableCell>
                          <TableCell className="text-xs xl:text-sm">{atendente.total}</TableCell>
                          <TableCell className="text-xs xl:text-sm hidden lg:table-cell">{atendente.concluidas}</TableCell>
                          <TableCell className="text-xs xl:text-sm">R$ {atendente.faturamento.toFixed(2)}</TableCell>
                          <TableCell className="hidden xl:table-cell">
                            <Badge className="bg-purple-100 text-purple-800 text-xs">
                              {atendente.tempo_medio_horas > 0 ? `${atendente.tempo_medio_horas}h` : '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <Badge className="bg-blue-100 text-blue-800 text-xs">
                              {atendente.total > 0 ? ((atendente.concluidas / atendente.total) * 100).toFixed(1) : 0}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de Solicitações */}
          <Card>
            <CardHeader>
              <CardTitle>Detalhamento das Reservas ({dados.solicitacoes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs xl:text-sm">Cliente</TableHead>
                      <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Destino</TableHead>
                      <TableHead className="text-xs xl:text-sm">Tipo</TableHead>
                      <TableHead className="text-xs xl:text-sm hidden lg:table-cell">Uso</TableHead>
                      <TableHead className="text-xs xl:text-sm hidden 2xl:table-cell">Fornecedor</TableHead>
                      <TableHead className="text-xs xl:text-sm">Check-in</TableHead>
                      <TableHead className="text-xs xl:text-sm">Status</TableHead>
                      <TableHead className="text-xs xl:text-sm">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dados.solicitacoes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                          Nenhuma reserva encontrada com os filtros aplicados
                        </TableCell>
                      </TableRow>
                    ) : (
                      dados.solicitacoes.map((sol) => (
                        <TableRow key={sol.id}>
                          <TableCell className="font-medium text-xs xl:text-sm">{sol.cliente_nome}</TableCell>
                          <TableCell className="text-xs xl:text-sm text-slate-600 hidden xl:table-cell">{sol.cidade_destino || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={sol.tipo_cliente === 'Associado' ? 'default' : 'secondary'} className="text-xs">
                              {sol.tipo_cliente}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs xl:text-sm hidden lg:table-cell">{sol.tipo_uso}</TableCell>
                          <TableCell className="text-xs xl:text-sm text-slate-600 hidden 2xl:table-cell">{sol.fornecedor_nome || '-'}</TableCell>
                          <TableCell className="text-xs xl:text-sm">{formatDateBR(sol.check_in)}</TableCell>
                          <TableCell>
                            <Badge
                              className={`text-xs ${
                                sol.status === 'Concluída'
                                  ? 'bg-green-100 text-green-800'
                                  : sol.status === 'Cancelada'
                                  ? 'bg-red-100 text-red-800'
                                  : sol.status === 'Em Tratamento'
                                  ? 'bg-blue-100 text-blue-800'
                                  : sol.status === 'Montada'
                                  ? 'bg-purple-100 text-purple-800'
                                  : sol.status === 'Enviada'
                                  ? 'bg-cyan-100 text-cyan-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {sol.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs xl:text-sm">
                              <div className="font-medium">{sol.valor_cotacao.toFixed(2)}</div>
                              <div className="text-xs text-slate-500">{sol.valor_fornecedor ? sol.valor_fornecedor.toFixed(2) : '0.00'}</div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
